import React, { useState, useEffect, useRef } from 'react';

const rollingTexts = [
    "AI agents.",
    "AI APIs integrations.",
    "Web.",
    "Mobile.",
    "Desktop.",
    "Cloud."
];

const rollingTextsSmallScreens = [
    "AI agents.",
    "Web.",
    "Mobile.",
    "Desktop.",
    "Cloud."
];

interface RollingTextProps {
    underlineAnimDurationMs?: number;
}

const RollingText = React.memo(function RollingText({ underlineAnimDurationMs }: RollingTextProps) {
    const [index, setIndex] = useState(0);
    const [animating, setAnimating] = useState(false);
    const [highlightPos, setHighlightPos] = useState<number>(1); // 0..n offsets, start highlighting center
    const [itemHeight, setItemHeight] = useState(90);
    const [fontSize, setFontSize] = useState('4rem');
    const [isSmallScreen, setIsSmallScreen] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 1025 : false);
    const [showUnderline, setShowUnderline] = useState(false);
    const [textWidth, setTextWidth] = useState(0);
    const [bolden, setBolden] = useState(false);
    const [maxRollingWidth, setMaxRollingWidth] = useState(0);
    const textRef = useRef<HTMLDivElement>(null);
    const itemRef = useRef<HTMLDivElement | null>(null);
    const measureRefs = useRef<(HTMLDivElement | null)[]>([]);
    const startTimerRef = useRef<number | null>(null);
    const intervalRef = useRef<number | null>(null);
    const strokeWidth = 5;
    const svgPadding = strokeWidth;

    useEffect(() => {
        const boldenTimer = setTimeout(() => setBolden(true), 1000);
        // Ensure underline animation is triggered after SVG is rendered
        const underlineTimer = setTimeout(() => {
            if (textRef.current) {
                const rect = textRef.current.getBoundingClientRect();
                setTextWidth(Math.round(rect.width));
            }
            // Double requestAnimationFrame to guarantee browser registers initial state
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setShowUnderline(true);
                });
            });
        }, 2000);
        return () => {
            clearTimeout(boldenTimer);
            clearTimeout(underlineTimer);
        };
    }, []);

    // Responsive height, font size, and text array
    useEffect(() => {
        function handleResize() {
            const small = window.innerWidth < 1025;
            setIsSmallScreen(small);
            setFontSize(small ? '2.3rem' : '4rem');

            // measure after layout changes; if measure fails set small-screen fallback
            requestAnimationFrame(measureItem);
            requestAnimationFrame(() => {
                measureItem();
                measureMaxWidth();
            });
        }

        function measureItem() {
            if (itemRef.current) {
                const rect = itemRef.current.getBoundingClientRect();
                const h = Math.round(rect.height);
                if (h > 6 && h !== itemHeight) {
                    setItemHeight(h);
                } else if (window.innerWidth < 1025) {
                    // reliable small-screen fallback (matches CSS .rolling-item height)
                    setItemHeight(28);
                }
            } else if (window.innerWidth < 1025) {
                // no item ref yet, ensure a sensible small-screen height so container isn't 0
                setItemHeight(28);
            }
        }

        function measureMaxWidth() {
            // Only for small screens
            if (!isSmallScreen) {
                setMaxRollingWidth(0);
                return;
            }
            let maxW = 0;
            measureRefs.current.forEach(ref => {
                if (ref) {
                    const w = ref.getBoundingClientRect().width;
                    if (w > maxW) maxW = w;
                }
            });
            setMaxRollingWidth(Math.ceil(maxW));
        }

        handleResize();
        window.addEventListener('resize', handleResize);
        // attempt initial measurement after mount
        const mTO = window.setTimeout(() => {
            measureItem();
            measureMaxWidth();
        }, 60);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(mTO);
        };
    }, [isSmallScreen, fontSize]); // re-measure on screen size/font size change

    useEffect(() => {
        // keep measuring if font size or layout changed
        const measure = () => {
            if (itemRef.current) {
                const rect = itemRef.current.getBoundingClientRect();
                const h = Math.round(rect.height);
                if (h > 6 && h !== itemHeight) setItemHeight(h);
            }
            else if (isSmallScreen) {
                setItemHeight(28);
            }
        };
        // measure shortly after any index/anim change to keep sizes accurate
        const t = window.setTimeout(measure, 50);
        return () => clearTimeout(t);
    }, [fontSize, isSmallScreen, index, animating, itemHeight]);

    useEffect(() => {
        const transitionDuration = 600; // ms -> keep in sync with CSS timing below
        const pauseBetween = 1500;      // ms pause between moves

        const tick = () => {
            // start move: highlight bottom row (it will move into center)
            if (isSmallScreen) {
                setHighlightPos(0);
            } else {
                setHighlightPos(2); // bottom visible row (offset 2) becomes highlighted while moving
            }

            // small tick before animating so highlight class can apply
            requestAnimationFrame(() => {
                setAnimating(true);
            });

            // after the transform finishes, update index and reset animating,
            // then set highlight to center (which now contains the moved item)
            window.setTimeout(() => {
                const arr = isSmallScreen ? rollingTextsSmallScreens : rollingTexts;

                setIndex(i => {
                    setTimeout(() => {
                        setHighlightPos(isSmallScreen ? 0 : 1);
                    }, 0);
                    return (i + 1) % arr.length;
                });

                requestAnimationFrame(() => setAnimating(false));
            }, transitionDuration);
        };

        // start sequence
        startTimerRef.current = window.setTimeout(() => {
            tick();
            intervalRef.current = window.setInterval(tick, transitionDuration + pauseBetween);
        }, 600);

        return () => {
            // clear initial timer + interval
            if (startTimerRef.current != null) {
                clearTimeout(startTimerRef.current);
                startTimerRef.current = null;
            }
            if (intervalRef.current != null) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isSmallScreen]);

    const resolvedTexts = rollingTexts.filter(Boolean);
    const resolvedSmallScreenTexts = rollingTextsSmallScreens.filter(Boolean);
    const activeTexts = isSmallScreen ? resolvedSmallScreenTexts : resolvedTexts;
    const rowsToShow = 1;
    const itemsToRender = Array.from({ length: 1 + (animating ? 1 : 0) }, (_, i) => i);
    // Compute the text for each row, wrapping around
    const getText = (offset: number) => activeTexts[(index + offset) % activeTexts.length];

    return (
        <div
            className="rolling-text-row"
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                margin: isSmallScreen ? '0 auto 0 auto' : '0 auto 32px auto',
                height: `${itemHeight}px`,
                gap: 8,
                boxSizing: 'border-box',
                ...(underlineAnimDurationMs ? { ['--rolling-underline-duration' as any]: `${underlineAnimDurationMs}ms` } : {})
            }}
        >
            <div className={`rolling-label animated-underline-text${bolden ? ' boldening' : ''}`}
                style={{
                    display: 'grid',
                    gridTemplateAreas: `"first1 first2" "second second"`,
                    columnGap: "5px",
                    minWidth: maxRollingWidth ? `${maxRollingWidth + 800}px` : 'unset',
                    width: maxRollingWidth ? `${maxRollingWidth + 800}px` : 'unset',
                    whiteSpace: 'nowrap',
                    fontSize: isSmallScreen ? '2.3rem' : '4rem',
                    transition: 'min-width 0.2s, width 0.2s',
                    justifyContent: 'center',
                    margin: '0 auto'
                }}>
                <div className="underline-target"
                    style={{
                        gridArea: 'first1',
                        position: 'relative',
                        display: 'inline-block',
                        paddingBottom: 0,
                        marginBottom: 0,
                        verticalAlign: 'bottom',
                        textAlign: 'center'
                    }}>
                    <span ref={textRef}
                        style={{
                            display: 'inline-block',
                            verticalAlign: 'bottom',
                            paddingBottom: 0,
                            marginBottom: 0
                        }}>
                        AI-Powered</span>
                    <svg className="animated-underline-svg"
                        width={textWidth}
                        height="30"
                        style={{
                            position: 'absolute',
                            left: 0,
                            bottom: isSmallScreen ? -12 : -5,
                            display: textWidth ? 'flex' : 'none',
                            pointerEvents: 'none',
                            ...(underlineAnimDurationMs ? { ['--rolling-underline-duration' as any]: `${underlineAnimDurationMs}ms` } : {})
                        }}>
                        <defs>
                            <linearGradient id="leaf-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stopColor="#02ffff" />
                                <stop offset="100%" stopColor="#02ffff" />
                                {/* <stop offset="0%" stopColor="#0d9dda" />
                <stop offset="100%" stopColor="#0d9dda" /> */}
                            </linearGradient>
                        </defs>
                        <path
                            d={`M0,20
              Q${textWidth / 8},8 ${textWidth / 4},20
              Q${textWidth * 3 / 8},28 ${textWidth / 2},20
              Q${textWidth * 5 / 8},8 ${textWidth * 3 / 4},20
              Q${textWidth * 7 / 8},28 ${textWidth},20
            `}
                            stroke="url(#leaf-gradient)"
                            strokeWidth={strokeWidth}
                            fill="none"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="animated-underline-path"
                            style={{
                                strokeDasharray: textWidth,
                                strokeDashoffset: showUnderline ? 0 : textWidth,
                                transition: `stroke-dashoffset var(--rolling-underline-duration, 0.7s) linear`,
                                // animation: `draw 20s linear forwards`
                            }} />
                    </svg>
                </div>
                <div style={{ gridArea: 'first2', width: '130%' }}> Applications </div>
                <div style={{
                    gridArea: 'second',
                    display: 'flex',
                    alignItems: 'baseline', // align text and rolling item on baseline
                    gap: 10,
                    width: '100%',
                    minHeight: `${itemHeight}px`,
                    position: 'relative',
                    // justifyContent: 'center'
                }}>
                    <div style={{
                        color: '#fff',
                        lineHeight: `${itemHeight}px`,
                        minHeight: `${itemHeight}px`,
                        display: 'flex',
                        alignItems: 'baseline',
                        zIndex: 1
                    }}>Development:</div>
                    {/* Reserve vertical space for rolling item, keep in flex flow */}
                    <div style={{
                        position: 'relative',
                        width: maxRollingWidth ? `${maxRollingWidth}px` : '100%',
                        minWidth: maxRollingWidth ? `${maxRollingWidth}px` : '100%',
                        minHeight: `${itemHeight}px`,
                        height: `${itemHeight}px`,
                        display: 'flex',
                        alignItems: 'baseline',
                        zIndex: 0
                    }}>
                        {/* Hidden measurement div to reserve space, always present */}
                        <div style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            visibility: 'hidden',
                            pointerEvents: 'none',
                            height: 0,
                            width: 0,
                            overflow: 'hidden'
                        }}>
                            {rollingTextsSmallScreens.map((text, i) => (
                                <div
                                    key={text}
                                    ref={el => { measureRefs.current[i] = el; }}
                                    style={{
                                        fontWeight: 500,
                                        fontSize: fontSize,
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                        padding: 0,
                                        margin: 0
                                    }}>
                                    {text}
                                </div>))}
                        </div>
                        <div className="rolling-texts" style={{
                            position: 'absolute',
                            left: 0,
                            top: isSmallScreen ? 12 : 20,
                            width: maxRollingWidth ? `${maxRollingWidth}px` : '100%',
                            minWidth: maxRollingWidth ? `${maxRollingWidth}px` : '100%',
                            height: `${itemHeight}px`,
                            minHeight: `${itemHeight}px`,
                            overflow: 'hidden',
                            zIndex: 2
                        }}>
                            <div className="rolling-stack"
                                style={{
                                    position: animating ? 'absolute' : 'relative',
                                    top: animating ? 0 : undefined,
                                    left: animating ? 0 : undefined,
                                    width: '100%',
                                    height: `${itemHeight * itemsToRender.length}px`,
                                    transition: animating ? `transform ${620}ms linear` : 'none',
                                    transform: animating ? `translateY(-${itemHeight}px)` : 'translateY(0)',
                                    willChange: 'transform'
                                }}>
                                {itemsToRender.map(i => {
                                    const text = getText(i);
                                    // Only highlight the true center row (index 0 for small screens)
                                    const isCenter = i === 0;
                                    const isExtra = i === rowsToShow;
                                    return (
                                        <div
                                            key={text + '-' + i}
                                            ref={i === 0 ? itemRef : undefined}
                                            className="rolling-item"
                                            style={{
                                                height: `${itemHeight}px`,
                                                lineHeight: `${itemHeight}px`,
                                                fontWeight: 500,
                                                fontSize,
                                                color: isCenter ? '#02ffff' : '#fff',
                                                // color: isCenter ? '#0d9dda' : '#fff',
                                                // color: isCenter ? '#23d18b' : '#fff',   
                                                // color: isCenter ? '#00b3ff' : '#fff',
                                                transition: 'color 200ms',
                                                opacity: isExtra && !animating ? 0 : 1,
                                                pointerEvents: isExtra && !animating ? 'none' : 'auto',
                                                visibility: isExtra && !animating ? 'hidden' : 'visible',
                                                textAlign: 'left',
                                                width: '100%',
                                                minWidth: 0
                                            }}>
                                            {text}
                                        </div>);
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default RollingText;